<template>
  <div>
    <my-header></my-header>
  
    <div class="content-container-level-1">
      <h2>New rush</h2>

      <form
        v-if="isLoadedNormal"
        @submit.prevent="createRushesWithConfirmation"
      >
        <table class="rush-table">
          <tr>
            <th class="text-nowrap">
              <label for="player-group-selector">Segment</label>
            </th>
            <td colspan="2">
              <b-form-select
                id="player-group-selector"
                v-model="playerGroupId"
                :options="playerGroupOptions"
              />
            </td>
          </tr>

          <period-input-tbody
            v-model="firstPeriod"
            :beginMin="minPlannedStartDate"
            :endMin="minPlannedFinishDate"
            class="period"
          />

          <tr>
            <th>Duration</th>
            <td colspan="2" class="event-duration">{{durationStr}}</td>
          </tr>
        </table>

        <hr>

        <h5>Client min version</h5>
        <client-version
          :clientVersionInfo="clientVersionInfo"
          class="mb-2"
        />

        <p class="mb-2">
          {{clientMinVersionComment}}
        </p>
        <input class="form-control" v-model="clientMinVersion" required>

        <hr>

        <h5>
          <b-form-checkbox v-model="isRepeatRequired">
            Повторять по правилам
          </b-form-checkbox>
        </h5>

        <template v-if="isRepeatRequired">
          <div class="mb-3">
            <label for="pause" class="mb-1">Пауза между событиями (в часах)</label>
            <input id="pause" type="number" min="0" class="form-control" v-model="pauseHourStr">
          </div>

          <p class="mb-1">Повторять до даты</p>
          <moment-input-table
            v-model="repeatEndMoment"
            :min="repeatEndMomentMin"
            class="mb-3"
          />

          <template v-if="optionalPeriods !== null">
            <p class="mb-1">Все события, которые будут созданы.</p>
            <periods-table
              :periods="optionalPeriods"
            />
          </template>

        </template>

        <hr>

        <b-button :to="{name: 'rushes'}">Cancel</b-button>
        <b-button type="submit" variant="primary">Create</b-button>
      </form>

    </div>

    <loading-indicator v-if="isLoading"/>
  </div>
</template>

<script>
import { HTTP } from '../http-common.js';
import notifier from '../notifier.js';

import { DEFAULT_PLAYER_GROUP_ID      } from '../model/event.js';
import { getPlayerGroupOptionsVerbose } from '../model/event.js';

/**
 * @typedef { import('../service/confirm.js').ConfirmHandle } ConfirmHandle
 */
import { confirmAction } from '../service/confirm.js';

import { ClientVersionInfo } from '../model/version.js';

/**
 * @typedef { import('../time.js').DatePeriodObject } DatePeriodObject
 */
import { DatePeriodObject_getDurationStr } from '../time.js';

import { normalizeVersionStr  } from '../util.js';
import { floorMomentTo5Min    } from '../util.js';
import { Period               } from '../util.js';
import { parseUint32          } from '../util.js';
import { convertHourToSec     } from '../util.js';
import { convertMomentToStamp } from '../util.js';
import { getRepeatedPeriods   } from '../util.js';

import MyHeader from './Header.vue';
import LoadingIndicator from './LoadingIndicator.vue';
import ClientVersion from './ClientVersion.vue';

import PeriodInputTbody from './util/PeriodInput.vue';
import MomentInputTable from './util/MomentInputTable.vue';
import PeriodsTable from './PeriodsTable.vue';

const LOADING_STATUS__INIT    = 1;
const LOADING_STATUS__LOADING = 2;
const LOADING_STATUS__NORMAL  = 3;
const LOADING_STATUS__ERROR   = 4;

export default {
  name: 'RushAdd',

  components: {
    MyHeader,
    LoadingIndicator,
    ClientVersion,
    PeriodInputTbody,
    MomentInputTable,
    PeriodsTable,
  },

  data() {
    const now = new Date();
    const begin = floorMomentTo5Min(now);

    // By default rush duration is 60 hours (2 days 12 hours).
    const DEFAULT_DURATION_HOURS = 60;

    const end = new Date(begin);
    end.setHours(end.getHours() + DEFAULT_DURATION_HOURS);

    /** @type {DatePeriodObject} */
    const firstPeriod = {
      begin: begin,
      end:   end,
    };

    return {
      loadingStatus: LOADING_STATUS__INIT,

      /** @type {?ClientVersionInfo} */
      clientVersionInfo: null,

      firstPeriod: firstPeriod,
      minPlannedStartDate:  begin,
      minPlannedFinishDate: begin,

      clientMinVersion: '',

      playerGroupId: DEFAULT_PLAYER_GROUP_ID,
      playerGroupOptions: getPlayerGroupOptionsVerbose(),

      isRepeatRequired: false,
      pauseHourStr: '24',

      repeatEndMoment:    end,
      repeatEndMomentMin: begin,
    };
  },

  computed: {
    /** @returns {boolean} */
    isLoading() {
      return this.loadingStatus === LOADING_STATUS__LOADING;
    },

    /** @returns {boolean} */
    isLoadedNormal() {
      return this.loadingStatus === LOADING_STATUS__NORMAL;
    },

    /** @returns {string} */
    durationStr() {
      return DatePeriodObject_getDurationStr(this.firstPeriod);
    },

    /** @returns {?string} */
    clientMinVersionComment() {
      const clientVersionInfo = this.clientVersionInfo;
      if(clientVersionInfo === null) {
        return null;
      }

      if(clientVersionInfo.gameVersion === clientVersionInfo.forcedVersion) {
        let message = '';
        message += 'Client min version of new rush must be inside this range. ';
        message += 'Server version and forced version are equal. '
        message += 'Consequently, value ' + clientVersionInfo.gameVersion + ' is the only option for min client version.';
        return message;
      }

      return 'Specify client min version of new rush inside this range.';
    },

    /** @returns {?Period[]} */
    optionalPeriods() {
      const [periods, err] = this.periods;
      if(err !== null) {
        return null;
      }
      return periods;
    },

    /** @returns {[?Period[], ?Error]} */
    periods() {
      /** @type {?Period} */
      let firstPeriod;

      /** @type {?Error} */
      let err;

      [firstPeriod, err] = this.validFirstStampPeriod;
      if(err !== null) {
        return [null, err];
      }
      if(firstPeriod === null) {
        return [null, new Error('firstPeriod === null')];
      }

      if(!this.isRepeatRequired) {
        const periods = [firstPeriod];
        return [periods, null];
      }

      /** @type {?number} */
      let pauseSec;

      [pauseSec, err] = this.pauseSec;
      if(err !== null) {
        return [null, err];
      }
      if(pauseSec === null) {
        return [null, new Error('pauseSec === null')];
      }

      /** @type {?number} */
      let repeatEndStamp;
      [repeatEndStamp, err] = this.repeatEndStamp;
      if(err !== null) {
        return [null, err];
      }
      if(repeatEndStamp === null) {
        return [null, new Error('repeatEndStamp === null')];
      }

      if(firstPeriod.end > repeatEndStamp) {
        return [null, new Error('First period end must be before repeat end.')];
      }

      const periods = getRepeatedPeriods(firstPeriod, pauseSec, repeatEndStamp);
      return [periods, null];
    },

    /** @returns {[?Period, ?Error]} */
    validFirstStampPeriod() {
      const firstDatePeriod = this.firstPeriod;
      const begin = convertMomentToStamp(firstDatePeriod.begin);
      const end   = convertMomentToStamp(firstDatePeriod.end);

      if(end === begin) {
        return [null, new Error('Rush duration is zero.')];
      }
      if(end <= begin) {
        return [null, new Error('End time must be after begin time.')];
      }

      return [new Period(begin, end), null];
    },

    /** @returns {[?number, ?Error]} */
    pauseHour() {
      const pauseHour = parseUint32(this.pauseHourStr);
      if(pauseHour === null) {
        return [null, new Error('Pause between events is invalid.')];
      }
      return [pauseHour, null];
    },

    /** @returns {[?number, ?Error]} */
    pauseSec() {
      const [pauseHour, err] = this.pauseHour;
      if(err !== null) {
        return [null, err];
      }
      if(pauseHour === null) {
        return [null, new Error('pauseHour === null')];
      }

      const pauseSec = convertHourToSec(pauseHour);
      return [pauseSec, null];
    },

    /** @returns {[?number, ?Error]} */
    repeatEndStamp() {
      const repeatEndMoment = this.repeatEndMoment;
      if(repeatEndMoment === null) {
        return [null, new Error('Repeat end moment is invalid.')];
      }

      const stamp = convertMomentToStamp(repeatEndMoment);
      return [stamp, null];
    },
  },

  created() {
    this.loadNewRushInfo();
  },

  methods: {
    loadNewRushInfo() {
      notifier.deleteSenderNotifications(this);

      this.loadingStatus = LOADING_STATUS__LOADING;

      HTTP.get('/new_rush_info')
      .then(response => {
        /** @type {NewRushInfo} */
        const data = response.data.data;

        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        const gameVersion = normalizeVersionStr(data.gameVersion);
        if(gameVersion === null) {
          notifier.showError('Loaded gameVersion is invalid.', {sender: this});
          return;
        }

        const forcedVersion = data.forcedVersion;
        this.clientVersionInfo = new ClientVersionInfo(gameVersion, forcedVersion);

        this.clientMinVersion = forcedVersion;

        this.loadingStatus = LOADING_STATUS__NORMAL;
      })
      .catch(err => {
        this.loadingStatus = LOADING_STATUS__ERROR;
        notifier.showError(err, {sender: this});
      });
    },

    createRushesWithConfirmation() {
      notifier.deleteSenderNotifications(this);

      const [rushesDef, err] = this.getRushesDef();
      if(err !== null) {
        notifier.showError(err, {sender: this});
        return;
      }
      if(rushesDef === null) {
        notifier.showError('Invalid input report. rushesDef === null.', {sender: this});
        return;
      }

      const rushAmount = rushesDef.amount;
      this.confirmRushesCreation(
        rushAmount,
        () => {
          this.createRushes(rushesDef);
        }
      );
    },

    /**
     * @returns {[?RushesDef, ?Error]}
     */
     getRushesDef() {
      const clientMinVersion = this.clientMinVersion.trim();
      if(clientMinVersion.length === 0) {
        return [null, new Error('Client min version must be specified.')];
      }

      const [periods, err] = this.periods;
      if(err !== null) {
        return [null, err];
      }
      if(periods === null) {
        return [null, new Error('periods === null')];
      }

      const rushesDef = new RushesDef(
        clientMinVersion,
        this.playerGroupId,
        periods
      );

      return [rushesDef, null];
    },

    /**
     * @param {number} rushAmount
     * @param {ConfirmHandle} onConfirmed
     */
    confirmRushesCreation(rushAmount, onConfirmed) {
      const target = (rushAmount === 1) ? 'this rush' : 'these rushes';
  
      confirmAction(
        this,
        `Are you sure you want to create ${target} {env}?`,
        'Create',
        onConfirmed,
      );
    },

    /** @param {RushesDef} rushesDef */
    createRushes(rushesDef) {
      HTTP.post('/rushes/add', rushesDef)
      .then(response => {
        const result = response.data.result.code;

        const RESULT_SUCCESS = 1;
        if(result === RESULT_SUCCESS) {
          const rushAmount = rushesDef.amount;
          const message = getRushesCreationConfirmationMessage(rushAmount);
          notifier.showSuccessAfterTransition(message);

          this.$router.push({name: 'rushes'});
          return;
        }

        notifier.showError(`Unknown result code ${result}.`, {sender: this});
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },
  },
};

/**
 * @typedef {Object} NewRushInfo
 * @property {string} gameVersion
 * @property {string} forcedVersion
 */


class RushesDef {
  /**
   * @param {?string}       clientMinVersion
   * @param {PlayerGroupId} playerGroupId
   * @param {Period[]}      periods
   */
  constructor(
    clientMinVersion,
    playerGroupId,
    periods
  ) {
    this.clientMinVersion = clientMinVersion;
    this.playerGroupId    = playerGroupId;
    this.periods          = periods;
  }

  // количество событий
  get amount() {
    return this.periods.length;
  }
}

function getRushesCreationConfirmationMessage(amount) {
  if(amount === 1) {
    return 'Rush created.';
  } else {
    return 'Rushes created.';
  }
}
</script>

<style scoped>
.rush-table {
  width: 100%;
}

.rush-table > tr > th {
  width: 0%;
  padding-right: .5rem;
}

.rush-table > tr > th > label {
  margin-bottom: 0;
}

.rush-table > tr:not(:last-child) >>> th,
.rush-table > tr:not(:last-child) >>> td {
  padding-bottom: .6rem;
}

.period >>> tr.end > th,
.period >>> tr.end > td {
  padding-bottom: .6rem;
}

.event-duration {
  padding-left: 0.8rem;
}
</style>
